import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import {
    Cancel,
    CloudDownload,
    Description,
    PictureAsPdf,
    Print,
    Refresh,
    ShoppingCart,
    Sync,
} from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useState } from "react"
import apiInstance from "../../../services/api"
import type {
    ItemRelatorioCompras,
    ItemRelatorioFiscal,
} from "../../../services/emissaoCupomFiscal"
import {
    baixarContabilidade,
    relatorioCompras,
    relatorioFiscalVendas,
} from "../../../services/emissaoCupomFiscal"
import useLoadingStore from "../../../stores/LoadingStore"
import { EmissaoCupomFiscalActionsTypes } from "../context/emissaoCupomFiscalActions"
import { useEmissaoCupomFiscalContext } from "../context/useEmissaoCupomFiscalContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "1100px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    chip: {
        margin: theme.spacing(0.5),
        fontWeight: 600,
    },
    actionBtn: {
        borderRadius: 20,
        margin: theme.spacing(0.5),
        textTransform: "none",
    },
    saveBtn: {
        borderRadius: 25,
        backgroundColor: "#4caf50",
        color: "white",
        boxShadow: "0 4px 10px rgba(76,175,80,0.3)",
        "&:hover": { backgroundColor: "#43a047" },
        textTransform: "none",
        margin: theme.spacing(0.5),
    },
    cellStatus: {
        fontWeight: 600,
        textTransform: "uppercase",
    },
    nomeCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    mensagemCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: 200,
    },
}))

function statusColor(status: string): "default" | "primary" | "secondary" {
    const s = status.toUpperCase()
    if (s === "CONCLUIDO") return "primary"
    if (s === "REJEITADO" || s === "CANCELADO") return "secondary"
    return "default"
}

function formatarValor(v: number | null): string {
    if (v == null) return "-"
    return `R$ ${v.toFixed(2).replace(".", ",")}`
}

export function CuponsFiscaisTabela() {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()
    const setLoading = useLoadingStore(s => s.setLoading)

    const {
        dados,
        dispatch,
        carregarCupons,
        sincronizarCupons,
        atualizarPendentes,
        cancelarCupom,
        reimprimirCupom,
    } = useEmissaoCupomFiscalContext()

    const { cupons, filtros } = dados

    // Contadores
    const qtdConcluidas = cupons.filter(c => c.status.toUpperCase() === "CONCLUIDO").length
    const qtdPendentes = cupons.filter(c =>
        ["PENDENTE", "GERANDO"].includes(c.status.toUpperCase())
    ).length
    const qtdErros = cupons.filter(c => c.status.toUpperCase() === "REJEITADO").length
    const temPendentes = qtdPendentes > 0
    const totalEmitido = cupons
        .filter(c => c.status.toUpperCase() === "CONCLUIDO")
        .reduce((acc, c) => acc + (c.valorTotal ?? 0), 0)
    const totalEmAndamento = cupons
        .filter(c => ["PENDENTE", "GERANDO"].includes(c.status.toUpperCase()))
        .reduce((acc, c) => acc + (c.valorTotal ?? 0), 0)

    // Estado dos modals
    const [modalContabilidade, setModalContabilidade] = useState(false)
    // Contabilidade fecha o mês passado: abre por padrão no mês ANTERIOR (trata a virada
    // de janeiro -> dezembro do ano anterior via normalização do Date).
    const mesAnteriorContabilidade = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1)
    const [mesContabilidade, setMesContabilidade] = useState(mesAnteriorContabilidade.getMonth() + 1)
    const [anoContabilidade, setAnoContabilidade] = useState(mesAnteriorContabilidade.getFullYear())
    const [emailContabilidade, setEmailContabilidade] = useState("")

    const [modalRelatorioFiscal, setModalRelatorioFiscal] = useState(false)
    const [itensRelatorioFiscal, setItensRelatorioFiscal] = useState<ItemRelatorioFiscal[]>([])

    const [modalRelatorioCompras, setModalRelatorioCompras] = useState(false)
    const [itensRelatorioCompras, setItensRelatorioCompras] = useState<ItemRelatorioCompras[]>([])

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    const handleFiltrar = () => {
        carregarCupons(filtros)
    }

    const handleBaixarPDF = (idNfce: number, idPedido: number | null) => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        apiInstance
            .get(`notafiscal/BaixarPDFNfce/${idNfce}`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: autorizacao,
                },
                responseType: "blob",
            })
            .then((response: { data: Blob }) => {
                const url = window.URL.createObjectURL(
                    new Blob([response.data], { type: "application/pdf" })
                )
                const link = document.createElement("a")
                link.href = url
                link.setAttribute("download", `CupomFiscal_${idPedido ?? idNfce}.pdf`)
                document.body.appendChild(link)
                link.click()
                link.remove()
                window.URL.revokeObjectURL(url)
            })
            .catch(() => alertStart("Erro ao baixar PDF", "error"))
    }

    const handleBaixarContabilidade = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setLoading(true)
        try {
            const response = await baixarContabilidade(
                autorizacao,
                mesContabilidade,
                anoContabilidade,
                emailContabilidade.trim() || undefined
            )
            if ("retornoErro" in response) {
                alertStart(response.mensagem || "Erro ao baixar contabilidade", "error")
            } else if (response instanceof Blob) {
                const url = window.URL.createObjectURL(response)
                const link = document.createElement("a")
                link.href = url
                link.setAttribute(
                    "download",
                    `cupons_${String(mesContabilidade).padStart(2, "0")}-${anoContabilidade}.zip`
                )
                document.body.appendChild(link)
                link.click()
                link.remove()
                window.URL.revokeObjectURL(url)
                setModalContabilidade(false)
            } else {
                alertStart((response as { mensagem: string }).mensagem, "info")
                setModalContabilidade(false)
            }
        } finally {
            setLoading(false)
        }
    }

    const handleRelatorioFiscal = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setLoading(true)
        try {
            const response = await relatorioFiscalVendas(
                autorizacao,
                filtros.dataInicio,
                filtros.dataFim
            )
            if ("retornoErro" in response) {
                alertStart(response.mensagem || "Erro ao gerar relatório", "error")
            } else {
                if (response.length === 0) {
                    alertStart("Nenhum XML encontrado para o período selecionado.", "info")
                } else {
                    setItensRelatorioFiscal(response)
                    setModalRelatorioFiscal(true)
                }
            }
        } finally {
            setLoading(false)
        }
    }

    const handleRelatorioCompras = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setLoading(true)
        try {
            const response = await relatorioCompras(
                autorizacao,
                filtros.dataInicio,
                filtros.dataFim
            )
            if ("retornoErro" in response) {
                alertStart(response.mensagem || "Erro ao gerar relatório de compras", "error")
            } else {
                if (response.length === 0) {
                    alertStart("Nenhuma nota de compra encontrada no período.", "info")
                } else {
                    setItensRelatorioCompras(response)
                    setModalRelatorioCompras(true)
                }
            }
        } finally {
            setLoading(false)
        }
    }

    const totalRelatorioFiscal = {
        quantidade: itensRelatorioFiscal.reduce((a, i) => a + i.quantidade, 0),
        vendidoPor: itensRelatorioFiscal.reduce((a, i) => a + i.vendidoPor, 0),
        valorCofins: itensRelatorioFiscal.reduce((a, i) => a + i.valorCofins, 0),
    }

    const totalRelatorioCompras = {
        total: itensRelatorioCompras.reduce((a, i) => a + i.total, 0),
        icms: itensRelatorioCompras.reduce((a, i) => a + i.icms, 0),
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                {/* Filtros */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Description className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Filtros
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={2} alignItems="flex-end">
                            <Grid item xs={6} sm={3} md={2}>
                                <TextField
                                    label="Data inicial"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={filtros.dataInicio}
                                    onChange={e => {
                                        const value = e.target.value
                                        dispatch({
                                            type: EmissaoCupomFiscalActionsTypes.SET_FILTRO,
                                            payload: { dataInicio: value },
                                        })
                                    }}
                                    placeholder="dd/mm/aaaa"
                                />
                            </Grid>
                            <Grid item xs={6} sm={3} md={2}>
                                <TextField
                                    label="Data final"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={filtros.dataFim}
                                    onChange={e => {
                                        const value = e.target.value
                                        dispatch({
                                            type: EmissaoCupomFiscalActionsTypes.SET_FILTRO,
                                            payload: { dataFim: value },
                                        })
                                    }}
                                    placeholder="dd/mm/aaaa"
                                />
                            </Grid>
                            <Grid item xs={6} sm={3} md={2}>
                                <TextField
                                    label="Nº início"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={filtros.numeroInicio}
                                    onChange={e => {
                                        const value = e.target.value
                                        dispatch({
                                            type: EmissaoCupomFiscalActionsTypes.SET_FILTRO,
                                            payload: { numeroInicio: value },
                                        })
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6} sm={3} md={2}>
                                <TextField
                                    label="Nº fim"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={filtros.numeroFim}
                                    onChange={e => {
                                        const value = e.target.value
                                        dispatch({
                                            type: EmissaoCupomFiscalActionsTypes.SET_FILTRO,
                                            payload: { numeroFim: value },
                                        })
                                    }}
                                />
                            </Grid>
                            <Grid item xs={8} sm={5} md={3}>
                                <TextField
                                    label="Identificador"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={filtros.identificador}
                                    onChange={e => {
                                        const value = e.target.value
                                        dispatch({
                                            type: EmissaoCupomFiscalActionsTypes.SET_FILTRO,
                                            payload: { identificador: value },
                                        })
                                    }}
                                />
                            </Grid>
                            <Grid item xs={8} sm={4} md={2}>
                                <TextField
                                    select
                                    label="Status"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={filtros.tipo}
                                    onChange={e => {
                                        const value = e.target.value
                                        dispatch({
                                            type: EmissaoCupomFiscalActionsTypes.SET_FILTRO,
                                            payload: { tipo: value },
                                        })
                                    }}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="1">CONCLUIDO</MenuItem>
                                    <MenuItem value="2">REJEITADO</MenuItem>
                                    <MenuItem value="3">GERANDO</MenuItem>
                                    <MenuItem value="4">CANCELADO</MenuItem>
                                    <MenuItem value="5">PENDENTE</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={4} sm={2} md={1}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    fullWidth
                                    onClick={handleFiltrar}
                                >
                                    Filtrar
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                {/* Contadores e ações */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Description className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Solicitações NFC-e — {filtros.dataInicio} a {filtros.dataFim}
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Box display="flex" flexWrap="wrap" mb={2}>
                            <Chip
                                className={classes.chip}
                                label={`${qtdConcluidas} Concluídas`}
                                style={{ backgroundColor: "#4caf50", color: "white" }}
                            />
                            <Chip
                                className={classes.chip}
                                label={`${qtdPendentes} Pendentes`}
                                style={{ backgroundColor: "#ff9800", color: "white" }}
                            />
                            <Chip
                                className={classes.chip}
                                label={`${qtdErros} Rejeitadas`}
                                style={{ backgroundColor: "#f44336", color: "white" }}
                            />
                        </Box>

                        {temPendentes && (
                            <Box
                                mb={2}
                                p={1.5}
                                style={{ backgroundColor: "#e3f2fd", borderRadius: 8 }}
                            >
                                <Typography variant="body2" style={{ color: "#1565c0" }}>
                                    Existem cupons aguardando confirmação da PlugNotas. Use{" "}
                                    <strong>Atualizar pendentes</strong> para consultar manualmente.
                                </Typography>
                            </Box>
                        )}

                        <Box display="flex" flexWrap="wrap" mb={2}>
                            <Button
                                className={classes.actionBtn}
                                variant="outlined"
                                color="primary"
                                startIcon={<Refresh />}
                                onClick={() => carregarCupons(filtros)}
                            >
                                Atualizar
                            </Button>
                            <Button
                                className={classes.actionBtn}
                                variant="outlined"
                                color="default"
                                startIcon={<Sync />}
                                onClick={sincronizarCupons}
                            >
                                Sincronizar cupons emitidos
                            </Button>
                            <Button
                                className={classes.actionBtn}
                                variant="outlined"
                                color="default"
                                startIcon={<CloudDownload />}
                                onClick={() => setModalContabilidade(true)}
                            >
                                Baixar contabilidade
                            </Button>
                            <Button
                                className={classes.actionBtn}
                                variant="outlined"
                                color="default"
                                startIcon={<Description />}
                                onClick={handleRelatorioFiscal}
                            >
                                Relatório fiscal de vendas
                            </Button>
                            <Button
                                className={classes.actionBtn}
                                variant="outlined"
                                style={{ borderColor: "#ff9800", color: "#ff9800" }}
                                startIcon={<ShoppingCart />}
                                onClick={handleRelatorioCompras}
                            >
                                Relatório de compras
                            </Button>
                            {temPendentes && (
                                <Button
                                    className={classes.actionBtn}
                                    variant="outlined"
                                    style={{ borderColor: "#ff9800", color: "#ff9800" }}
                                    startIcon={<Sync />}
                                    onClick={atualizarPendentes}
                                >
                                    Atualizar pendentes
                                </Button>
                            )}
                        </Box>

                        {/* Tabela */}
                        <TableContainer
                            style={{ maxHeight: 520, overflowX: "auto" }}
                        >
                            <Table
                                size="small"
                                stickyHeader
                                style={{ width: "100%", minWidth: 1220 }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ minWidth: 90 }}>Pedido</TableCell>
                                        <TableCell style={{ minWidth: 160 }}>Cliente</TableCell>
                                        <TableCell style={{ minWidth: 120 }}>Data/Hora</TableCell>
                                        <TableCell style={{ minWidth: 120 }}>Data pedido</TableCell>
                                        <TableCell style={{ minWidth: 100 }}>Valor</TableCell>
                                        <TableCell style={{ minWidth: 90 }}>Nº NFCe</TableCell>
                                        <TableCell style={{ minWidth: 140 }}>
                                            Identificador
                                        </TableCell>
                                        <TableCell style={{ minWidth: 120 }}>Situação</TableCell>
                                        <TableCell style={{ minWidth: 160 }}>Mensagem</TableCell>
                                        <TableCell style={{ minWidth: 120, whiteSpace: "nowrap" }}>
                                            Ações
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {cupons.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={10} align="center">
                                                <Typography
                                                    variant="body2"
                                                    style={{ color: "#7f8c8d", padding: "16px 0" }}
                                                >
                                                    Nenhuma solicitação de cupom fiscal encontrada
                                                    no período.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {cupons.map(cupom => {
                                        const statusUp = cupom.status.toUpperCase()
                                        const isConcluido = statusUp === "CONCLUIDO"
                                        const rowStyle: React.CSSProperties = isConcluido
                                            ? { backgroundColor: "#f1f8e9" }
                                            : statusUp === "PENDENTE" || statusUp === "GERANDO"
                                            ? { backgroundColor: "#fff8e1" }
                                            : statusUp === "REJEITADO" || statusUp === "CANCELADO"
                                            ? { backgroundColor: "#fce4ec" }
                                            : {}
                                        return (
                                            <TableRow key={cupom.idNfce} style={rowStyle}>
                                                <TableCell>#{cupom.idPedido ?? "-"}</TableCell>
                                                <TableCell
                                                    className={classes.nomeCell}
                                                    title={cupom.nomeCliente ?? ""}
                                                >
                                                    {cupom.nomeCliente ?? "-"}
                                                </TableCell>
                                                <TableCell>
                                                    {cupom.dataSolicitacao ?? "-"}
                                                </TableCell>
                                                <TableCell>{cupom.dataRealizacao ?? "-"}</TableCell>
                                                <TableCell>
                                                    {formatarValor(cupom.valorTotal)}
                                                </TableCell>
                                                <TableCell>{cupom.numeroNota ?? "-"}</TableCell>
                                                <TableCell
                                                    className={classes.nomeCell}
                                                    title={cupom.identificador ?? ""}
                                                >
                                                    {cupom.identificador ?? "-"}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="caption"
                                                        className={classes.cellStatus}
                                                        style={{
                                                            color: isConcluido
                                                                ? "#2e7d32"
                                                                : statusUp === "PENDENTE" ||
                                                                  statusUp === "GERANDO"
                                                                ? "#e65100"
                                                                : statusUp === "REJEITADO" ||
                                                                  statusUp === "CANCELADO"
                                                                ? "#c62828"
                                                                : "#555",
                                                        }}
                                                    >
                                                        {cupom.status}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    className={classes.mensagemCell}
                                                    title={cupom.mensagem ?? ""}
                                                >
                                                    {cupom.mensagem ?? "-"}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    style={{ whiteSpace: "nowrap" }}
                                                >
                                                    {isConcluido && (
                                                        <Tooltip title="Baixar PDF">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    handleBaixarPDF(
                                                                        cupom.idNfce,
                                                                        cupom.idPedido
                                                                    )
                                                                }
                                                            >
                                                                <PictureAsPdf
                                                                    style={{
                                                                        color: "#dc3545",
                                                                        fontSize: 20,
                                                                    }}
                                                                />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {isConcluido && (
                                                        <Tooltip title="Reimprimir">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    reimprimirCupom(cupom.idNfce)
                                                                }
                                                            >
                                                                <Print
                                                                    style={{
                                                                        color: "#1565c0",
                                                                        fontSize: 20,
                                                                    }}
                                                                />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {isConcluido && (
                                                        <Tooltip title="Cancelar cupom">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => {
                                                                    if (
                                                                        window.confirm(
                                                                            "Deseja realmente solicitar o cancelamento deste cupom fiscal? Esta operação é irreversível."
                                                                        )
                                                                    ) {
                                                                        cancelarCupom(cupom.idNfce)
                                                                    }
                                                                }}
                                                            >
                                                                <Cancel
                                                                    style={{
                                                                        color: "#6c757d",
                                                                        fontSize: 20,
                                                                    }}
                                                                />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Totais */}
                        {cupons.length > 0 && (
                            <Box mt={2} display="flex" flexWrap="wrap" style={{ gap: 16 }}>
                                <Typography variant="body2">
                                    <strong>Total emitido (CONCLUÍDO):</strong>{" "}
                                    {formatarValor(totalEmitido)}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Total em andamento:</strong>{" "}
                                    {formatarValor(totalEmAndamento)}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Box>

            {/* Modal Baixar Contabilidade */}
            <Dialog
                open={modalContabilidade}
                onClose={() => setModalContabilidade(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Baixar arquivo para contabilidade</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                        Selecione o mês/ano de referência. Os XMLs serão baixados direto do
                        armazenamento (S3).
                    </Typography>
                    <TextField
                        select
                        label="Mês"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={mesContabilidade}
                        onChange={e => setMesContabilidade(Number(e.target.value))}
                        style={{ marginBottom: 16 }}
                    >
                        {[
                            "Janeiro",
                            "Fevereiro",
                            "Março",
                            "Abril",
                            "Maio",
                            "Junho",
                            "Julho",
                            "Agosto",
                            "Setembro",
                            "Outubro",
                            "Novembro",
                            "Dezembro",
                        ].map((m, i) => (
                            <MenuItem key={i + 1} value={i + 1}>
                                {m}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Ano"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={anoContabilidade}
                        onChange={e => setAnoContabilidade(Number(e.target.value))}
                        style={{ marginBottom: 16 }}
                    />
                    <TextField
                        label="Enviar por e-mail (opcional)"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={emailContabilidade}
                        onChange={e => setEmailContabilidade(e.target.value)}
                        placeholder="seu@email.com"
                        helperText="Se preenchido, o arquivo será enviado por e-mail em vez de baixado."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModalContabilidade(false)}>Cancelar</Button>
                    <Button className={classes.saveBtn} onClick={handleBaixarContabilidade}>
                        Baixar / Enviar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal Relatório Fiscal de Vendas */}
            <Dialog
                open={modalRelatorioFiscal}
                onClose={() => setModalRelatorioFiscal(false)}
                maxWidth="xl"
                fullWidth
            >
                <DialogTitle>
                    Relatório fiscal de vendas — {filtros.dataInicio} a {filtros.dataFim}
                </DialogTitle>
                <DialogContent style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <TableContainer style={{ overflowX: "auto" }}>
                        <Table size="small" style={{ width: "100%", minWidth: 1200 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{ minWidth: 80 }}>Cód</TableCell>
                                    <TableCell style={{ minWidth: 80 }}>ID</TableCell>
                                    <TableCell style={{ minWidth: 180 }}>Descrição</TableCell>
                                    <TableCell style={{ minWidth: 90 }}>NCM</TableCell>
                                    <TableCell style={{ minWidth: 90 }}>CFOP</TableCell>
                                    <TableCell style={{ minWidth: 100 }}>CST/CSOSN</TableCell>
                                    <TableCell style={{ minWidth: 90 }}>Qtd</TableCell>
                                    <TableCell style={{ minWidth: 110 }}>Vendido Por (R$)</TableCell>
                                    <TableCell style={{ minWidth: 90 }}>%PIS</TableCell>
                                    <TableCell style={{ minWidth: 90 }}>Vr. PIS</TableCell>
                                    <TableCell style={{ minWidth: 100 }}>%COFINS</TableCell>
                                    <TableCell style={{ minWidth: 100 }}>Vr. COFINS</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {itensRelatorioFiscal.map((item, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{item.identificadorCupom}</TableCell>
                                        <TableCell>{item.id}</TableCell>
                                        <TableCell
                                            className={classes.nomeCell}
                                            title={item.descricao}
                                        >
                                            {item.descricao}
                                        </TableCell>
                                        <TableCell>{item.ncm}</TableCell>
                                        <TableCell>{item.cfop}</TableCell>
                                        <TableCell>{item.cst}</TableCell>
                                        <TableCell>{item.quantidade.toFixed(2)}</TableCell>
                                        <TableCell>{item.vendidoPor.toFixed(2)}</TableCell>
                                        <TableCell>{item.percentualPis.toFixed(2)}</TableCell>
                                        <TableCell>{item.valorPis.toFixed(2)}</TableCell>
                                        <TableCell>{item.percentualCofins.toFixed(2)}</TableCell>
                                        <TableCell>{item.valorCofins.toFixed(2)}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <strong>TOTAIS</strong>
                                    </TableCell>
                                    <TableCell>
                                        <strong>
                                            {totalRelatorioFiscal.quantidade.toFixed(2)}
                                        </strong>
                                    </TableCell>
                                    <TableCell>
                                        <strong>
                                            {totalRelatorioFiscal.vendidoPor.toFixed(2)}
                                        </strong>
                                    </TableCell>
                                    <TableCell />
                                    <TableCell />
                                    <TableCell />
                                    <TableCell>
                                        <strong>
                                            {totalRelatorioFiscal.valorCofins.toFixed(2)}
                                        </strong>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModalRelatorioFiscal(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>

            {/* Modal Relatório de Compras */}
            <Dialog
                open={modalRelatorioCompras}
                onClose={() => setModalRelatorioCompras(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    Relatório de Compras — {filtros.dataInicio} a {filtros.dataFim}
                </DialogTitle>
                <DialogContent style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <TableContainer style={{ overflowX: "auto" }}>
                        <Table size="small" style={{ width: "100%", minWidth: 850 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{ minWidth: 90 }}>Número</TableCell>
                                    <TableCell style={{ minWidth: 80 }}>Série</TableCell>
                                    <TableCell style={{ minWidth: 70 }}>Mod</TableCell>
                                    <TableCell style={{ minWidth: 120 }}>Emissão</TableCell>
                                    <TableCell style={{ minWidth: 90 }}>CFOP</TableCell>
                                    <TableCell style={{ minWidth: 200 }}>Fornecedor</TableCell>
                                    <TableCell style={{ minWidth: 100 }}>Total (R$)</TableCell>
                                    <TableCell style={{ minWidth: 100 }}>ICMS (R$)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {itensRelatorioCompras.map((item, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{item.numero}</TableCell>
                                        <TableCell>{item.serie}</TableCell>
                                        <TableCell>{item.mod}</TableCell>
                                        <TableCell>{item.emissao}</TableCell>
                                        <TableCell>{item.cfop}</TableCell>
                                        <TableCell
                                            className={classes.nomeCell}
                                            title={item.fornecedor}
                                        >
                                            {item.fornecedor}
                                        </TableCell>
                                        <TableCell>{item.total.toFixed(2)}</TableCell>
                                        <TableCell>{item.icms.toFixed(2)}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <strong>TOTAIS</strong>
                                    </TableCell>
                                    <TableCell>
                                        <strong>{totalRelatorioCompras.total.toFixed(2)}</strong>
                                    </TableCell>
                                    <TableCell>
                                        <strong>{totalRelatorioCompras.icms.toFixed(2)}</strong>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModalRelatorioCompras(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
